import * as React from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import { ALL_HEALTH_SIGNAL_KINDS, type HealthSignalKind } from '@etiquetachef/types';
import { formatInt } from '@/features/dashboard-finance/formatters';
import { HealthBadge } from './HealthBadge';
import { HealthFilters } from './HealthFilters';
import { HealthList } from './HealthList';
import { useHealth } from './useHealth';
import type { TenantHealthSnapshot } from '@etiquetachef/types';

function HealthBody({
  counts,
  snapshots,
  selected,
  onSelectChange,
}: {
  counts: Record<HealthSignalKind, number>;
  snapshots: readonly TenantHealthSnapshot[];
  selected: readonly HealthSignalKind[];
  onSelectChange: (next: readonly HealthSignalKind[]) => void;
}): React.JSX.Element {
  return (
    <SpaceBetween size="l">
      <ColumnLayout columns={6}>
        {ALL_HEALTH_SIGNAL_KINDS.map((kind) => (
          <Container key={kind}>
            <SpaceBetween size="xxs">
              <HealthBadge kind={kind} />
              <Box variant="h2">{formatInt(counts[kind])}</Box>
            </SpaceBetween>
          </Container>
        ))}
      </ColumnLayout>
      <HealthFilters selected={selected} onChange={onSelectChange} />
      <HealthList snapshots={snapshots} />
    </SpaceBetween>
  );
}

/**
 * Tela `/dashboard/health`: contagem de tenants por sinal de saúde (ZOMBIE,
 * PRE_CHURN_RISK…) do último dia calculado, e a lista dos problemáticos com
 * filtro por sinal.
 *
 * Origem: sem issue — commit 030a61a79, 2026-05-04 (rota /dashboard/health).
 *
 * Contrato: sem props e sem período — mostra sempre o snapshot mais recente.
 * Filtro por sinal em estado local (não vai para a URL): vazio = todos;
 * senão, tenant com QUALQUER um dos sinais marcados. Sem doc → aviso
 * "sem dados".
 *
 * Usado por: `routes/router.tsx` (rota `dashboard/health`, só super-admin).
 *
 * Arquitetura: só apresentação; leitura de `aggregations/health/days` no
 * `useHealth`. Os mesmos sinais alimentam as abas churn-risk/zombie de
 * `/dashboard/customers`.
 */
export function HealthDashboardPage(): React.JSX.Element {
  const { t } = useTranslation();
  const state = useHealth();
  const [selected, setSelected] = useState<readonly HealthSignalKind[]>([]);

  const filtered = useMemo(() => {
    if (state.kind !== 'READY' || !state.health) return [];
    if (selected.length === 0) return state.health.topProblematic;
    return state.health.topProblematic.filter((s) => s.signals.some((k) => selected.includes(k)));
  }, [state, selected]);

  return (
    <ContentLayout
      header={<Header variant="h1" description={t('health.subtitle')}>{t('health.title')}</Header>}
    >
      {state.kind === 'LOADING' && (
        <Box textAlign="center" padding="l">
          <Spinner size="large" />
        </Box>
      )}
      {state.kind === 'ERROR' && <Alert type="error">{state.error}</Alert>}
      {state.kind === 'READY' && !state.health && (
        <Box color="text-body-secondary">{t('health.noData')}</Box>
      )}
      {state.kind === 'READY' && state.health && (
        <HealthBody
          counts={state.health.countsBySignal}
          snapshots={filtered}
          selected={selected}
          onSelectChange={setSelected}
        />
      )}
    </ContentLayout>
  );
}
